import React, { useState } from 'react';
import AgTechTourHelpButton from '@agtech/web/components/App/Tour/AgTechTourHelpButton';
import { SaleContactCreditDialogTourConfig } from './SaleContactCreditDialogTourConfig';
import dayjs from 'dayjs';
import { AgTechDataActionSuccessfulExecutionHandlingProps } from '@agtech/core/data/actions/AgTechDataActions';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormDateControl } from '@agtech/web/components/Forms/Controls/AgTechFormDateControl';
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl';
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormToggleControl } from '@agtech/web/components/Forms/Controls/AgTechFormToggleControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';
import { ContactCredit, DefaultContactCredit } from '@sales/data/operation/contacts/credits/ContactCreditData';
import { useContactCreditDataWriteCreditAction } from '@sales/data/operation/contacts/credits/ContactCreditDataActions';
import { useSaleContactDataGetContactDetailsQuery } from '@sales/data/operation/contacts/SaleContactDataQueries';
import { ContactDetails, getContactFullName } from '@agtech/data/contacts/ContactData';

export type ContactCreditDialogProps = {
    contactId: number,
    creditId?: number,
    onCreditSubmitted?: (props: AgTechDataActionSuccessfulExecutionHandlingProps<ContactCredit, ContactCredit>) => Promise<void>
}

type ContactCreditDialogModel = {
    credit: ContactCredit,
    context: {
        contactDetails: ContactDetails,
        hasExpiration: boolean
    }
}

const ContactCreditDialog = (props: ContactCreditDialogProps) => {
    const dialogQuery = useSaleContactDataGetContactDetailsQuery(props.contactId);
    const dialogAction = useContactCreditDataWriteCreditAction(props.contactId, props.onCreditSubmitted);

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: {
                    header: props.creditId ? 'Update Credit' : 'Create Credit',
                    settings: {
                        content: {
                            body: () => <AgTechTourHelpButton tourConfig={SaleContactCreditDialogTourConfig} />
                        }
                    }
                },
                submitButtonText: props.creditId ? 'Update' : 'Create'
            })}
            dialogData={{
                query: dialogQuery,
                getDialogData: contactDetails => {
                    const contactCredits = Object.values(contactDetails.credits);

                    const credit = contactCredits.find(c => c.id === props.creditId) ?? { 
                        ...DefaultContactCredit,
                        contactId: contactDetails.contact.id
                    };

                    const dialogData: ContactCreditDialogModel = {
                        credit: {
                            ...credit,
                            expires: credit?.expires ? new Date(credit.expires) : undefined,
                        },
                        context: {
                            contactDetails: contactDetails,
                            hasExpiration: credit?.expires !== undefined
                        }
                    }

                    return dialogData;
                }
            }}
            dialogAction={{
                action: dialogAction,
                getActionData: d => d.credit
            }}
        >
            {(formContext) => {
                const [expiresVisible, setExpiresVisibility] = useState<boolean>(formContext.entity.context.hasExpiration);

                return (
                    <AgTechFormBody>
                        <div id='contact-credit-amount'>
                        <AgTechFormRow>
                            <AgTechFormReadOnlyTextControl
                                entityType={formContext.entity}
                                caption='Contact'
                                value={e => getContactFullName(e.context.contactDetails.contact)}
                            />
                            <AgTechFormNumberControl
                                entityType={formContext.entity}
                                caption='Amount'
                                value={e => e.credit.creditAmount}
                                required={true}
                                unit="$"
                                validation={{
                                    validateOnSubmit: (entity, val) => val <= 0 ? 'Credit amount must be greater than zero' : ''
                                }}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    credit: {
                                        ...e.credit,
                                        creditAmount: val
                                    }
                                })}
                            />
                        </AgTechFormRow>
                        </div>
                        <div id='contact-credit-description'>
                        <AgTechFormRow>
                            <AgTechFormTextAreaControl
                                entityType={formContext.entity}
                                caption='Description'
                                value={e => e.credit.description}
                                rows={4}
                                width={100}
                                required={true}
                                onControlValueChanged={async (e, val) => ({
                                    ...e,
                                    credit: {
                                        ...e.credit,
                                        description: val
                                    }
                                })}
                                resizable={false}
                            />
                        </AgTechFormRow>
                        </div>
                        <div id='contact-credit-details'>
                        <AgTechFormSection header='Credit Details'>
                            <AgTechFormRow>
                                <AgTechFormToggleControl
                                    entityType={formContext.entity}
                                    caption='Expires?'
                                    value={() => expiresVisible}
                                    onControlValueChanged={async (entity, value) => {
                                        setExpiresVisibility(value);
    
                                        return {
                                            ...entity,
                                            credit: {
                                                ...entity.credit,
                                                expires: value ? new Date() : undefined
                                            },
                                            hasExpiration: value
                                        };
                                    }}
                                />
                                {expiresVisible ? (
                                    <AgTechFormDateControl
                                        entityType={formContext.entity}
                                        caption="Expiration Date" 
                                        value={d => dayjs(d.credit.expires ?? new Date()).format('MM-DD-YYYY')} 
                                        width={50}
                                        readonly={!expiresVisible}
                                        allowPreviousDates={false}
                                        onControlValueChanged={async (e, val) => ({
                                            ...e,
                                            credit: {
                                                ...e.credit,
                                                expires: dayjs(val).toDate()
                                            }
                                        })}
                                    />
                                ) : (
                                    <AgTechFormReadOnlyTextControl
                                        entityType={formContext.entity}
                                        caption="Expiration Date" 
                                        value={e => e.context.hasExpiration ? dayjs(e.credit.expires).format('MM-DD-YYYY') : ''} 
                                    />
                                )}
                            </AgTechFormRow>
                            <AgTechFormRow>
                                <AgTechFormTextAreaControl
                                    entityType={formContext.entity}
                                    caption='Private Note'
                                    value={e => e.credit.privateNote}
                                    rows={4}
                                    width={100}
                                    required={false}
                                    onControlValueChanged={async (e, val) => ({
                                        ...e,
                                        credit: {
                                            ...e.credit,
                                            privateNote: val
                                        }
                                    })}
                                    resizable={false}
                                />
                            </AgTechFormRow>
                        </AgTechFormSection>
                        </div>
                    </AgTechFormBody>
                )
            }}
        </AgTechFormDialog>
    )
}

ContactCreditDialog.Open = (e: AgTechClickEventHandler, props: ContactCreditDialogProps) => e.dialogs.openDialog({
    dialog: () => <ContactCreditDialog {...props} />
});

ContactCreditDialog.Button = (props: ContactCreditDialogProps & AgTechButtonProps) => {
    let buttonProps: AgTechButtonProps = {
        text: props.creditId ? 'Update Credit' : 'Create Credit',
        icon: props.creditId ? 'fas fa-edit' : 'fas fa-plus',
        ...props,
        action: e => ContactCreditDialog.Open(e, props)
    };

    return <AgTechButton {...buttonProps} />
}

export default ContactCreditDialog;